// src/pages/admin/AdminRolesPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Role,
    RoleScope,
    Permission,
    listRoles,
    listPermissions,
    createRole,
    updateRole,
    setRolePermissions,
    deleteRole,
} from 'src/features/admin/api';
import { useToast } from 'src/shared/ui/toast/useToast';

const scopeLabels: Record<RoleScope, string> = {
    PROJECT: 'Project',
    COMPANY: 'Company',
    // RESOURCE: 'Resource',
    SERVICE: 'Service',
};

const AdminRolesPage: React.FC = () => {
    const toast = useToast();

    const [roles, setRoles] = useState<Role[]>([]);
    const [perms, setPerms] = useState<Permission[]>([]);
    const [loading, setLoading] = useState(false);

    const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

    // 새 Role 입력용
    const [newScope, setNewScope] = useState<RoleScope>('PROJECT');
    const [newName, setNewName] = useState('');
    const [newDesc, setNewDesc] = useState('');

    // 선택된 Role의 permissionIds 편집용
    const [editingPermIds, setEditingPermIds] = useState<string[]>([]);
    const [savingPerms, setSavingPerms] = useState(false);

    const selectedRole = useMemo(() => roles.find((r) => r.id === selectedRoleId) ?? null, [roles, selectedRoleId]);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [r, p] = await Promise.all([listRoles(), listPermissions()]);
            setRoles(r);
            setPerms(p);
            if (!selectedRoleId && r.length > 0) {
                setSelectedRoleId(r[0].id);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadAll();
    }, []);

    useEffect(() => {
        if (!selectedRole) {
            setEditingPermIds([]);
            return;
        }
        setEditingPermIds(selectedRole.grants.map((g) => g.permissionId));
    }, [selectedRole]);

    const togglePermId = (id: string) => {
        setEditingPermIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleSavePerms = async () => {
        if (!selectedRole) return;
        setSavingPerms(true);
        try {
            const updated = await setRolePermissions(selectedRole.id, editingPermIds);
            setRoles((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
            toast.success(`Role "${updated.name}"의 권한을 저장했습니다.`, {
                title: '저장 완료',
            });
        } catch (e: any) {
            toast.error(e?.response?.data?.error?.message ?? '권한 저장 중 오류가 발생했습니다.', {
                title: '저장 실패',
            });
        } finally {
            setSavingPerms(false);
        }
    };

    const handleCreateRole = async () => {
        if (!newName.trim()) {
            toast.warning('Role 이름을 입력해 주세요.', {
                title: '입력 필요',
            });
            return;
        }
        setLoading(true);
        try {
            const created = await createRole({
                scope: newScope,
                name: newName.trim(),
                description: newDesc.trim() || undefined,
            });
            setRoles((prev) => [...prev, created]);
            setNewName('');
            setNewDesc('');
            setSelectedRoleId(created.id);
            toast.success(`Role "${created.name}"를 추가했습니다.`, {
                title: '추가 완료',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleQuickEditMeta = async (role: Role) => {
        const newDesc = window.prompt('Role 설명(description)을 수정하세요', role.description ?? '');
        if (newDesc === null) return;

        setLoading(true);
        try {
            const updated = await updateRole(role.id, { description: newDesc || undefined });
            setRoles((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
            toast.success(`Role "${updated.name}" 설명을 수정했습니다.`, {
                title: '수정 완료',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRole = async (role: Role) => {
        if (!window.confirm(`정말 Role "${role.name}"(${scopeLabels[role.scope]}) 을/를 삭제하시겠습니까?`)) return;
        setLoading(true);
        try {
            await deleteRole(role.id);
            setRoles((prev) => prev.filter((r) => r.id !== role.id));
            if (selectedRoleId === role.id) {
                setSelectedRoleId(null);
            }
            toast.success(`Role "${role.name}"를 삭제했습니다.`, {
                title: '삭제 완료',
            });
        } catch (e: any) {
            toast.error('삭제할 수 없습니다. (멤버/Company 등에 연결되어 있을 수 있습니다)', {
                title: '삭제 실패',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-gray-900">Roles &amp; Permissions</h1>
                {loading && <span className="text-xs text-gray-500">Loading...</span>}
            </div>

            {/* 새 Role 추가 */}
            <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-end">
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Scope</label>
                    <select
                        className="border rounded px-2 py-1 text-sm"
                        value={newScope}
                        onChange={(e) => setNewScope(e.target.value as RoleScope)}
                    >
                        <option value="PROJECT">Project</option>
                        <option value="COMPANY">Company</option>
                        {/* <option value="RESOURCE">Resource</option> */}
                        <option value="SERVICE">Service</option>
                    </select>
                </div>
                <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Role Name</label>
                    <input
                        className="w-full border rounded px-2 py-1 text-sm font-mono"
                        placeholder="예: PROJECT_OWNER, PM, VIEWER"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Description</label>
                    <input
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="역할 설명"
                        value={newDesc}
                        onChange={(e) => setNewDesc(e.target.value)}
                    />
                </div>
                <button
                    className="px-3 py-2 rounded bg-sky-600 text-white text-sm hover:bg-sky-700"
                    onClick={handleCreateRole}
                >
                    Role 추가
                </button>
            </div>

            {/* 좌측: Role 리스트 / 우측: 선택 Role 권한 설정 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Role 목록 */}
                <div className="lg:col-span-1 border rounded-lg bg-white shadow-sm overflow-hidden">
                    <div className="px-3 py-2 border-b text-xs font-semibold text-gray-600 bg-gray-50">Roles</div>
                    <ul className="divide-y">
                        {roles.length === 0 && (
                            <li className="px-3 py-4 text-sm text-gray-400">아직 Role이 없습니다.</li>
                        )}
                        {roles.map((r) => {
                            const selected = r.id === selectedRoleId;
                            const permCount = r.grants.length;
                            return (
                                <li
                                    key={r.id}
                                    className={
                                        'px-3 py-2 text-sm cursor-pointer grid //flex //items-center //justify-between ' +
                                        (selected ? 'bg-sky-50' : 'hover:bg-gray-50')
                                    }
                                    style={{ gridTemplateColumns: '150px 1fr 80px' }} // 1열 고정, 2열 유동, 3열 고정
                                    onClick={() => setSelectedRoleId(r.id)}
                                >
                                    <div className="min-w-0">
                                        <div className="font-mono text-xs text-gray-900 truncate">{r.name}</div>
                                        <div className="text-[11px] text-gray-500 truncate">
                                            {scopeLabels[r.scope]} · {permCount} perms
                                        </div>
                                    </div>
                                    <div className="min-w-0 text-[11px] text-gray-700 truncate">
                                        {r.description ?? ''}
                                    </div>
                                    <div className="justify-self-end space-x-1 text-[11px] whitespace-nowrap">
                                        <button
                                            className="text-sky-700 hover:underline"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                void handleQuickEditMeta(r);
                                            }}
                                        >
                                            설명
                                        </button>
                                        <button
                                            className="text-rose-600 hover:underline"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                void handleDeleteRole(r);
                                            }}
                                        >
                                            삭제
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* 선택 Role 권한 편집 */}
                <div className="lg:col-span-2 border rounded-lg bg-white shadow-sm overflow-hidden">
                    <div className="px-3 py-2 border-b text-xs font-semibold text-gray-600 bg-gray-50 flex items-center justify-between">
                        <span>Permissions for selected Role</span>
                        {selectedRole && (
                            <span className="text-[11px] text-gray-500">
                                {scopeLabels[selectedRole.scope]} · {selectedRole.name}
                            </span>
                        )}
                    </div>

                    {!selectedRole && <div className="p-4 text-sm text-gray-400">왼쪽에서 Role을 선택하세요.</div>}

                    {selectedRole && (
                        <div className="p-4 space-y-3">
                            <div className="max-h-80 overflow-auto border rounded-md">
                                <table className="min-w-full text-xs">
                                    <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Use</th>
                                            <th className="px-3 py-2 text-left">Key</th>
                                            <th className="px-3 py-2 text-left">Category</th>
                                            <th className="px-3 py-2 text-left">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {perms.length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="px-3 py-4 text-center text-gray-400 text-xs">
                                                    Permission 리스트가 비어 있습니다. 먼저 Permission을 추가하세요.
                                                </td>
                                            </tr>
                                        )}
                                        {perms.map((p) => {
                                            const checked = editingPermIds.includes(p.id);
                                            return (
                                                <tr key={p.id} className="border-t">
                                                    <td className="px-3 py-1 align-middle">
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => togglePermId(p.id)}
                                                        />
                                                    </td>
                                                    <td className="px-3 py-1 font-mono">{p.key}</td>
                                                    <td className="px-3 py-1 text-gray-500">{p.category ?? '-'}</td>
                                                    <td className="px-3 py-1 text-gray-700">{p.description ?? '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    className="px-3 py-1.5 rounded bg-sky-600 text-white text-xs hover:bg-sky-700 disabled:opacity-60"
                                    disabled={savingPerms}
                                    onClick={handleSavePerms}
                                >
                                    {savingPerms ? '저장 중…' : '권한 매핑 저장'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminRolesPage;
